import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { payrollPayments, payrolls } from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_UK = ['', 'січень', 'лютий', 'березень', 'квітень', 'травень',
  'червень', 'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

const METHOD_LABEL: Record<string, string> = {
  CASH: 'Готівкою з каси',
  BANK: 'На банківські рахунки (зарплатний проект)',
};

export default function PayrollPaymentPrintPage() {
  const { id } = useParams();

  const docQ = useQuery({
    queryKey: ['payroll-payment', id ?? ''],
    queryFn: () => payrollPayments.get(id!),
    enabled: !!id,
  });
  const payrollQ = useQuery({
    queryKey: ['payroll', docQ.data?.payroll_id ?? ''],
    queryFn: () => payrolls.get(docQ.data!.payroll_id!),
    enabled: !!docQ.data?.payroll_id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const empQ = useQuery({
    queryKey: ['employees', docQ.data?.organization_id ?? ''],
    queryFn: () => refs.employees(docQ.data!.organization_id),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const empName = useMemo(() => {
    const m = new Map<string, { name: string; code: string | null; position?: string | null }>();
    empQ.data?.forEach((e) => m.set(e.id, { name: e.name, code: e.code, position: e.position_name }));
    return m;
  }, [empQ.data]);

  if (!docQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const doc = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === doc.organization_id);
  const periodLabel = payrollQ.data
    ? `${MONTHS_UK[payrollQ.data.period_month]} ${payrollQ.data.period_year}`
    : null;

  const total = doc.items.reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '10pt', lineHeight: 1.3,
      maxWidth: '210mm', margin: '0 auto', background: 'white',
    }}>
      <div className="print-hide" style={{
        position: 'sticky', top: 0, background: '#f1f5f9',
        padding: '0.5rem 1rem', marginBottom: '1rem', borderRadius: 6,
        display: 'flex', gap: '0.5rem', alignItems: 'center',
      }}>
        <strong>Платіжна відомість на виплату ЗП</strong>
        <button onClick={() => window.print()} className="primary" style={{ marginLeft: 'auto' }}>
          🖨 Друкувати
        </button>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '10pt' }}>{org?.full_name ?? org?.name ?? ''}</div>
        {org?.edrpou && (
          <div style={{ fontSize: '9pt', color: '#555' }}>ЄДРПОУ: {org.edrpou}</div>
        )}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '0.5rem 0' }}>
        ПЛАТІЖНА ВІДОМІСТЬ
      </h2>
      <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>
        № {doc.document_number} від {new Date(doc.document_date).toLocaleDateString('uk-UA')}
      </p>
      {periodLabel && (
        <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>
          на виплату заробітної плати за {periodLabel} р.
        </p>
      )}
      <p style={{ textAlign: 'center', margin: '0.3rem 0', fontSize: '9pt', color: '#555' }}>
        Спосіб виплати: {METHOD_LABEL[doc.payment_method] ?? doc.payment_method}
      </p>

      <table style={{
        width: '100%', borderCollapse: 'collapse', fontSize: '9pt', marginTop: '1rem',
        border: '1px solid #000',
      }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            <th style={{ border: '1px solid #000', padding: '0.3rem', width: '5%' }}>№</th>
            <th style={{ border: '1px solid #000', padding: '0.3rem', width: '10%' }}>Таб. №</th>
            <th style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'left' }}>
              Прізвище, ім'я, по батькові
            </th>
            <th style={{ border: '1px solid #000', padding: '0.3rem', width: '18%' }}>
              Сума до виплати, грн
            </th>
            <th style={{ border: '1px solid #000', padding: '0.3rem', width: '25%' }}>
              Підпис про отримання
            </th>
          </tr>
        </thead>
        <tbody>
          {doc.items.map((it) => {
            const emp = empName.get(it.employee_id);
            return (
              <tr key={it.id ?? it.line_number}>
                <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'center' }}>
                  {it.line_number}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'center' }}>
                  {emp?.code ?? '—'}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.3rem' }}>
                  {emp?.name ?? '—'}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right' }}>
                  {fmt(it.amount)}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.3rem', minHeight: 24 }}></td>
              </tr>
            );
          })}
          <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
            <td colSpan={3} style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right' }}>
              Усього до виплати:
            </td>
            <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right' }}>
              {fmt(total)}
            </td>
            <td style={{ border: '1px solid #000' }}></td>
          </tr>
        </tbody>
      </table>

      <p style={{ marginTop: '1rem' }}>
        За цією відомістю видано суму у розмірі&nbsp;
        <strong>{fmt(total)}</strong> грн ({moneyToUkrWords(total)}).
      </p>
      <p style={{ marginTop: '0.5rem' }}>
        Кількість одержувачів: <strong>{doc.items.length}</strong>.
      </p>

      <table style={{ width: '100%', marginTop: '3rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <div>Видав:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>
                (посада, підпис, П.І.Б.)
              </div>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <div>Перевірив (бухгалтер):</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>
                (підпис, П.І.Б.)
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// Сума прописом — спрощена реалізація (тільки гривні + копійки).
function moneyToUkrWords(value: number): string {
  const hrn = Math.floor(value);
  const kop = Math.round((value - hrn) * 100);
  return `${numberToUkrWords(hrn)} грн ${String(kop).padStart(2, '0')} коп.`;
}

const ONES = ['', 'один', 'два', 'три', 'чотири', "п'ять", 'шість', 'сім', 'вісім', "дев'ять"];
const ONES_F = ['', 'одна', 'дві', 'три', 'чотири', "п'ять", 'шість', 'сім', 'вісім', "дев'ять"];
const TEENS = ['десять', 'одинадцять', 'дванадцять', 'тринадцять', 'чотирнадцять',
  "п'ятнадцять", 'шістнадцять', 'сімнадцять', 'вісімнадцять', "дев'ятнадцять"];
const TENS = ['', '', 'двадцять', 'тридцять', 'сорок', "п'ятдесят",
  'шістдесят', 'сімдесят', 'вісімдесят', "дев'яносто"];
const HUNDREDS = ['', 'сто', 'двісті', 'триста', 'чотириста', "п'ятсот",
  'шістсот', 'сімсот', 'вісімсот', "дев'ятсот"];

function triadToWords(n: number, feminine: boolean): string {
  if (n === 0) return '';
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const o = n % 10;
  const parts: string[] = [];
  if (h) parts.push(HUNDREDS[h]!);
  if (t === 1) {
    parts.push(TEENS[o]!);
  } else {
    if (t) parts.push(TENS[t]!);
    if (o) parts.push((feminine ? ONES_F[o] : ONES[o])!);
  }
  return parts.join(' ');
}

function thousandsForm(n: number): string {
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return 'тисяч';
  if (last === 1) return 'тисяча';
  if (last >= 2 && last <= 4) return 'тисячі';
  return 'тисяч';
}

function millionsForm(n: number): string {
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return 'мільйонів';
  if (last === 1) return 'мільйон';
  if (last >= 2 && last <= 4) return 'мільйони';
  return 'мільйонів';
}

function numberToUkrWords(n: number): string {
  if (n === 0) return 'нуль';
  if (n < 0) return 'мінус ' + numberToUkrWords(-n);
  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const parts: string[] = [];
  if (millions) parts.push(`${triadToWords(millions, false)} ${millionsForm(millions)}`);
  if (thousands) parts.push(`${triadToWords(thousands, true)} ${thousandsForm(thousands)}`);
  if (rest) parts.push(triadToWords(rest, false));
  return parts.join(' ').trim();
}
